import type { FeatureCollection, Point } from "geojson";

import { MAP_DEFAULT_PATH_COLOR } from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { IEditorTool } from "./maplibre-polygon-editor.models";

/** Handed to the handle sources whenever handles are hidden, so their data keeps a stable identity. */
export const POLYGON_EDITOR_EMPTY_HANDLES: FeatureCollection<Point> = {
  type: "FeatureCollection",
  features: [],
};

/** Source and layer ids. One source feeds the shapes, one the vertices, one the midpoints. */
export const POLYGON_EDITOR_SHAPES_SOURCE_ID = "maplibre-polygon-editor-shapes";
export const POLYGON_EDITOR_FILL_LAYER_ID = "maplibre-polygon-editor-fill";
export const POLYGON_EDITOR_LINE_LAYER_ID = "maplibre-polygon-editor-line";
export const POLYGON_EDITOR_VERTICES_SOURCE_ID =
  "maplibre-polygon-editor-vertices";
export const POLYGON_EDITOR_VERTEX_LAYER_ID = "maplibre-polygon-editor-vertex";
export const POLYGON_EDITOR_MIDPOINTS_SOURCE_ID =
  "maplibre-polygon-editor-midpoints";
export const POLYGON_EDITOR_MIDPOINT_LAYER_ID =
  "maplibre-polygon-editor-midpoint";

/**
 * Geoman's .marker-icon: a 14px white disc rimmed in Leaflet's path colour, whatever colour the shape
 * itself is. Halved here because a MapLibre circle takes a radius.
 */
export const POLYGON_EDITOR_VERTEX_RADIUS_PX = 7;
export const POLYGON_EDITOR_VERTEX_STROKE_WIDTH_PX = 1;
export const POLYGON_EDITOR_VERTEX_FILL_COLOR = rydeColors.white;
export const POLYGON_EDITOR_VERTEX_STROKE_COLOR = MAP_DEFAULT_PATH_COLOR;

/** Geoman's .marker-icon-middle: the same disc at 10px and 70% opacity. */
export const POLYGON_EDITOR_MIDPOINT_RADIUS_PX = 5;
export const POLYGON_EDITOR_MIDPOINT_OPACITY = 0.7;

/**
 * Grab radius (px) around a handle. Querying the rendered circle alone would make the hit area exactly the
 * disc; geoman's handle was a DOM element of the same size, but a few forgiving pixels make dragging a
 * vertex on a dense boundary far less fiddly.
 */
export const POLYGON_EDITOR_HANDLE_GRAB_PADDING_PX = 4;

/**
 * How close (px) a dragged vertex has to come to another one before it snaps onto it — geoman's
 * snapDistance, which the areas editor passed explicitly.
 */
export const POLYGON_EDITOR_SNAP_DISTANCE_PX = 20;

/**
 * A ring needs three corners. Geoman refused to remove a vertex that would take a polygon below this.
 */
export const POLYGON_EDITOR_MIN_RING_VERTICES = 3;

/** Rendered size (px) of a toolbar glyph, inside geoman's 29px button with its 5px padding. */
export const MAPLIBRE_POLYGON_ICON_SIZE_PX = 19;

/**
 * Long-press on a shape's body (held roughly still) picks the whole shape up; a quicker press still pans
 * the map or reshapes as usual. Carried over unchanged from the Leaflet areas editor.
 */
export const POLYGON_EDITOR_LONG_PRESS_MS = 450;
export const POLYGON_EDITOR_LONG_PRESS_MOVE_TOLERANCE_PX = 6;

/**
 * The five traced geoman glyph outlines, keyed by the tool they belong to. Verbatim path data — the
 * toolbar's icons are pixel-for-pixel the ones the Leaflet toolbar drew.
 */
export const MAPLIBRE_POLYGON_TOOL_ICON_PATHS = {
  draw: "M19.4206892,9.16509725 C19.1523681,8.66992914 19,8.10275831 19,7.5 C19,5.56700338 20.5670034,4 22.5,4 C24.4329966,4 26,5.56700338 26,7.5 C26,9.26323595 24.6961471,10.7219407 23,10.9645556 L23,19.0354444 C24.6961471,19.2780593 26,20.736764 26,22.5 C26,24.4329966 24.4329966,26 22.5,26 C20.736764,26 19.2780593,24.6961471 19.0354444,23 L10.9645556,23 C10.7219407,24.6961471 9.26323595,26 7.5,26 C5.56700338,26 4,24.4329966 4,22.5 C4,20.5670034 5.56700338,19 7.5,19 C8.10275831,19 8.66992914,19.1523681 9.16509725,19.4206892 L19.4206892,9.16509725 Z M20.8349073,10.5793063 L10.5793108,20.8349027 C10.6086731,20.8890888 10.6366469,20.9441372 10.6631844,21 L19.3368156,21 C19.6825775,20.272154 20.272154,19.6825775 21,19.3368156 L21,10.6631844 C20.9441372,10.6366469 20.8890888,10.6086731 20.8349027,10.5793108 Z M22.5,9 C23.3284271,9 24,8.32842712 24,7.5 C24,6.67157288 23.3284271,6 22.5,6 C21.6715729,6 21,6.67157288 21,7.5 C21,8.32842712 21.6715729,9 22.5,9 Z M22.5,24 C23.3284271,24 24,23.3284271 24,22.5 C24,21.6715729 23.3284271,21 22.5,21 C21.6715729,21 21,21.6715729 21,22.5 C21,23.3284271 21.6715729,24 22.5,24 Z M7.5,24 C8.32842712,24 9,23.3284271 9,22.5 C9,21.6715729 8.32842712,21 7.5,21 C6.67157288,21 6,21.6715729 6,22.5 C6,23.3284271 6.67157288,24 7.5,24 Z",
  reshape:
    "M13.5,11 C11.5670034,11 10,9.43299662 10,7.5 C10,5.56700338 11.5670034,4 13.5,4 C15.4329966,4 17,5.56700338 17,7.5 C17,9.43299662 15.4329966,11 13.5,11 Z M13.5,9 C14.3284271,9 15,8.32842712 15,7.5 C15,6.67157288 14.3284271,6 13.5,6 C12.6715729,6 12,6.67157288 12,7.5 C12,8.32842712 12.6715729,9 13.5,9 Z M12.0002889,7.52973893 C12.0125983,8.16273672 12.4170197,8.6996643 12.9807111,8.90767966 L3,15 L3,13 L12.0002889,7.52973893 Z M14.2172722,6.18228472 L19.453125,3 L22.6589355,3 L14.989102,7.68173885 C14.9962971,7.62216459 15,7.56151472 15,7.5 C15,6.93138381 14.6836098,6.4366645 14.2172722,6.18228472 Z M23.4434042,19.2851736 L20.1282799,19.2851736 L21.8729983,23.5349525 C21.9945296,23.8295773 21.8556546,24.1599209 21.5778734,24.2849208 L20.0414675,24.9545142 C19.7550613,25.0795141 19.4338738,24.9366704 19.3123426,24.6509518 L17.6544367,20.6154541 L14.9461873,23.4010151 C14.5852811,23.7721711 14,23.4860463 14,22.9992653 L14,9.57183533 C14,9.05933561 14.6225311,8.809492 14.946156,9.17008555 L23.8340292,18.3120179 C24.1925291,18.6613615 23.9279979,19.2851736 23.4434042,19.2851736 Z",
  move: "M21,14 L21,10 L27,15 L21,20 L21,16 L16,16 L16,21 L20,21 L15,27 L10,21 L14,21 L14,16 L9,16 L9,20 L3,15 L9,10 L9,14 L14,14 L14,9 L10,9 L15,3 L20,9 L16,9 L16,14 L21,14 Z",
  cut: "M12.9691574,13.4939435 L21.0317032,5.54167013 L23.4649499,5.67722957 L17.0470713,14.5106816 L27.5660336,17.1333535 L25.7891944,18.8012588 L14.5854951,17.8987506 L13.6487955,19.188007 C13.794639,19.2650958 13.9367985,19.3534417 14.0741377,19.4532245 C15.6379648,20.5894114 15.9846357,22.7782052 14.8484488,24.3420324 C13.7122619,25.9058595 11.5234681,26.2525304 9.95964096,25.1163435 C8.39581384,23.9801565 8.04914296,21.7913627 9.18532986,20.2275356 C9.74587276,19.4560145 10.5626188,18.9807475 11.4341218,18.8336407 L12.6805656,17.1180579 L12.5239724,16.3747216 L11.9506932,15.3012391 L9.89310646,14.7882251 C9.13093796,15.2357261 8.19977854,15.3966447 7.27445355,15.1659352 C5.39887519,14.698301 4.25751094,12.7987519 4.72514515,10.9231736 C5.19277935,9.04759519 7.09232846,7.90623094 8.96790682,8.37386515 C10.8434852,8.84149935 11.9848494,10.7410485 11.5172152,12.6166268 C11.4761464,12.7813449 11.4240335,12.9404001 11.3618627,13.0931999 L12.9691574,13.4939435 Z M7.75829735,13.2253438 C8.56211664,13.4257584 9.37620912,12.9366023 9.57662378,12.132783 C9.77703844,11.3289637 9.28788233,10.5148713 8.48406303,10.3144566 C7.68024373,10.1140419 6.86615126,10.603198 6.6657366,11.4070173 C6.46532194,12.2108366 6.95447805,13.0249291 7.75829735,13.2253438 Z M10.8033639,21.4031061 C10.3164266,22.0733177 10.4649998,23.0113722 11.1352115,23.4983095 C11.8054231,23.9852467 12.7434776,23.8366735 13.2304148,23.1664619 C13.7173521,22.4962502 13.5687788,21.5581957 12.8985672,21.0712585 C12.2283556,20.5843212 11.2903011,20.7328945 10.8033639,21.4031061 Z",
  delete:
    "M17.7874219,18.4812552 L11.6480079,13.3498184 L6.40466009,19.3816001 L10.5539156,22.9884929 L13.86934,22.9884929 L17.7874219,18.4812552 Z M16.5074252,22.9884929 L26.0000002,22.9884929 L26.0000002,24.9884929 L10.0000002,24.9884929 L9.80708313,24.9884929 L5.09254204,20.8910192 C4.25891285,20.1663564 4.17057814,18.9031112 4.89524093,18.069482 L16.0482444,5.23941916 C16.7729072,4.40578998 18.0361525,4.31745526 18.8697816,5.04211806 L24.9074583,10.2905903 C25.7410875,11.0152531 25.8294222,12.2784983 25.1047594,13.1121275 L16.5074252,22.9884929 Z",
} as const;

/** The scissors are drawn upright and rotated into place, as geoman drew them. */
export const MAPLIBRE_POLYGON_CUT_ICON_TRANSFORM =
  "translate(16.093194, 15.663351) rotate(-32.000000) translate(-16.093194, -15.663351)";

export /**
 * Geoman's five tools, in its two control bars: the draw tool on its own, then the four that act on shapes
 * already on the map.
 */
const EDITOR_TOOL_GROUPS: IEditorTool[][] = [
  [
    {
      mode: "draw",
      translationKey: "area.mapToolDrawPolygon",
      iconPath: MAPLIBRE_POLYGON_TOOL_ICON_PATHS.draw,
    },
  ],
  [
    {
      mode: "edit",
      translationKey: "area.mapToolReshape",
      iconPath: MAPLIBRE_POLYGON_TOOL_ICON_PATHS.reshape,
    },
    {
      mode: "drag",
      translationKey: "area.mapToolMoveShape",
      iconPath: MAPLIBRE_POLYGON_TOOL_ICON_PATHS.move,
    },
    {
      mode: "cut",
      translationKey: "area.mapToolCutShape",
      iconPath: MAPLIBRE_POLYGON_TOOL_ICON_PATHS.cut,
      iconPathTransform: MAPLIBRE_POLYGON_CUT_ICON_TRANSFORM,
    },
    {
      mode: "remove",
      translationKey: "area.mapToolDeleteShape",
      iconPath: MAPLIBRE_POLYGON_TOOL_ICON_PATHS.delete,
    },
  ],
];
